/* eslint-disable no-useless-escape */
import * as React from 'react';
import {
	Modal,
	Button,
	Grid,
	XFilledIcon,
	Card,
	TrashcanIcon,
	PlusFilledIcon,
	Section,
	Checkbox
} from 'react-unity';
import './RuleModal.css';
import TextAreaField from '../../../common/form-controls/TextareaField';
import ToggleField from '../../../common/form-controls/ToggleField';
import TextInputField from '../../../common/form-controls/TextInputField';
import { AbstractRulesForm, IRuleStateForm, RulesFormProps } from './AbstractAWSRulesForm';
import { RuleAction } from '../../../../models/enums/POR/RuleAction';
import SelectField from '../../../common/form-controls/SelectField';
import { RuleOperation } from '../../../../models/enums/POR/RuleOperation';
import { PortOpeningRequestsDirection } from '../../../../models/enums/POR/PortOpeningRequestsDirection';
import MultipleSelectField from '../../../common/form-controls/MultipleSelectField';
import { AWSRuleRegion } from '../../../../models/enums/POR/AWSRuleRegion';

interface RuleModalAccToInternetProps extends RulesFormProps {
}

interface AccToInternetRuleStateForm extends IRuleStateForm {
	name: string;
	destinationURLs: string;
}
interface RuleModalAccToInternetState {
	stateForm: AccToInternetRuleStateForm;
	availableProtocols: string[];
}

const MAX_PROTOCOLS = 4;

export default class RuleModalAccToInternet extends AbstractRulesForm<
	RuleModalAccToInternetProps,
	RuleModalAccToInternetState
> {

	constructor(props: RuleModalAccToInternetProps) {
		super(props);
		this.state = {
			stateForm: this.props.isFromHistory ? this.getRuleCopy() : this.getDefaultForm(),
			availableProtocols: ['TCP', 'UDP', 'ICMP echo-request', 'ICMP echo-reply'],
		};

		this.initFormModel();
	}

	isURLValid = (url) => /^(http(s)?:\/\/)?(www\.)?[a-zA-ZA-Z0-9-]+(\.[a-zA-ZA-Z0-9-]+)+(\/[^\s]*)?$/.test(url);

	initFormModel() {
		super.initFormModel();
		this.formModel.addField('name', {
			getValue: () => this.state.stateForm.name,
			validation: {
				required: true,
				rules:
					[
						{
							assert: () => this.state.stateForm.operation !== RuleOperation.Add
								|| this.state.stateForm.name?.length <= 24,
							message: 'Name cannot be longer than 24 characters.',
						},
						{
							assert: () => !this.state.stateForm.name?.includes(' '),
							message: 'Name cannot contain spaces.',
						},
						{
							assert: () => this.state.stateForm.operation !== RuleOperation.Add
							|| /^(?![-_])[a-zA-Z][a-zA-Z-_]*(?<![-_])$/.test(this.state.stateForm.name ?? ''),
							message: 'Only letters (uppercase and lowercase), dashes, underscores are allowed. No trailing or beginning dashes or underscores.',
						}
					]
			},
		});	

		this.formModel.fields.sourceIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false
			}),
			this.IpIsInAddressSpacesRule()
		);

		this.formModel.fields.destinationIPs.validation.required =
			() => !(this.state.stateForm.destinationURLs?.trim().length > 0);
		
		this.formModel.fields.destinationIPs.validation.rules.push(
			this.IpInputValidationRule({
				onlyPrivateAddresses: false
			})
		);

		const numberOfDestinationItemsValidation = {
			assert: () => {
				const numberOfIPs = this.state.stateForm.destinationIPs?.split(';').filter(s => s !== '').length ?? 0;
				const numberOfURLs = this.state.stateForm.destinationURLs?.split(';').filter(s => s !== '').length ?? 0;
				const numberOfDestinationItems = numberOfIPs + numberOfURLs;
				const maxNumberOfDestinationItems = 50;
				return numberOfDestinationItems <= maxNumberOfDestinationItems;
			},
			message: "Total Destination URLs and IPs can't be greater than 50"
		}

		this.formModel.addField('destinationURLs', {
			getValue: () => this.parseMultipleValues(this.state.stateForm.destinationURLs),
			validation: {
				required: () => !(this.state.stateForm.destinationIPs?.trim().length > 0),
				rules: [
					{
						assert: () => !this.hasEmptyValues(this.state.stateForm.destinationURLs),
						message: 'Empty values are not allowed.'
					},
					{
						assert: () => !(this.state.stateForm.destinationURLs.includes('https://')
						|| this.state.stateForm.destinationURLs.includes('http://')),
						message: 'URL cannot have "http" or "https"'
					},
					{
						assert: (value) => {
							const urls = this.parseMultipleValues(value)?.split(';');
							return urls?.every(url => this.isURLValid(url));
						},
						message: (value) => {
							const invalidAddress = this.parseMultipleValues(value)?.split(';').find(url => !this.isURLValid(url));
							return `${invalidAddress} is an invalid URL.`;
						}
					},
					numberOfDestinationItemsValidation
				]
			}
		});

		this.formModel.fields.destinationIPs.validation.rules.push(numberOfDestinationItemsValidation);

		this.formModel.fields.destinationPorts.validation.rules = [
			{
				assert: () => this.state.stateForm?.destinationPorts[0].protocol !== '',
				message: 'Empty values are not allowed.'
			},
			{
				assert: () => !this.hasEmptyValues(this.state.stateForm?.destinationPorts[0].destinationPorts),
				message: 'Empty values are not allowed.'
			},
			{
				assert: () => {
					const ports = this.parseMultipleValues(this.state.stateForm?.destinationPorts[0].destinationPorts)?.split(';');
					return ports.every(port => this.isPortInputValid(port, 0));
				},
				message: () => {
					const invalidPort = this.parseMultipleValues(this.state.stateForm?.destinationPorts[0].destinationPorts).split(';').find(port => !this.isPortInputValid(port, 0));
					return `${invalidPort} is an invalid port input.`;
				}
			}
		]
	}

    getSourceIPOptions() {
		//const addressSpaces = this.props.virtualPrivateClouds.flatMap(vNet => vNet.cidr);
		const addressSpaces = [... new Set(this.props.virtualPrivateClouds.flatMap(vNet => vNet.cidr))];
		return addressSpaces;
	}

    getDestinationIPOptions() {
        return [];
    }

	// eslint-disable-next-line class-methods-use-this
	getDefaultFormState() {
		return {
			isTemporary: false,
			expirationDate: null,
			operation: RuleOperation.Add,
			name: '',
			destinationURLs: '',
			destinationPorts: [{ protocol: '', destinationPorts: '' }],
		};
	}

	getRegionOptions(): AWSRuleRegion[] {
		var accToInternetRegions = new AWSRuleRegion().awsListByDirection(PortOpeningRequestsDirection.AccountToInternet, this.props.virtualPrivateClouds);
		return accToInternetRegions;
	}

	addProtocol = () => {
		if (this.state.stateForm.destinationPorts.length < MAX_PROTOCOLS) {
			const updatedProtocols = [...this.state.stateForm.destinationPorts];
			updatedProtocols.push({ protocol: '', destinationPorts: '' });
			this.formModel.fields.destinationPorts.validation.rules.push(
				{
					assert: () => this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length-1]?.protocol !== '',
					message: 'Empty values are not allowed.'
				},
				{
					assert: () => !this.hasEmptyValues(this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length-1]?.destinationPorts),
					message: 'Empty values are not allowed.'
				},
				{
					assert: () => {
						const ports = this.parseMultipleValues(this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length-1]?.destinationPorts)?.split(';');
						return ports.every(port => this.isPortInputValid(port, 0));
					},
					message: () => {
						const invalidPort = this.parseMultipleValues(this.state.stateForm?.destinationPorts[this.state.stateForm?.destinationPorts.length-1]?.destinationPorts).split(';').find(port => !this.isPortInputValid(port, 0));
						return `${invalidPort} is an invalid port input.`;
					}
				}
			);
			this.handleStateFormChange('destinationPorts', updatedProtocols);
		}
	};

	handleProtocolChange = (index: number, field: string, value: string) => {
		const updatedProtocols = [...this.state.stateForm.destinationPorts];
		updatedProtocols[index][field] = value;
		this.handleStateFormChange('destinationPorts', updatedProtocols);
	};

	removeProtocol = (index: number) => {
		const updatedProtocols = this.state.stateForm.destinationPorts.filter((_, i) => i !== index);
		this.handleStateFormChange('destinationPorts', updatedProtocols);
		this.formModel.fields.destinationPorts.validation.rules = this.formModel.fields.destinationPorts.validation.rules?.filter((_, i) => i !== index);
	};

	// Rendering protocols dynamically
	renderProtocols() {
		return this.state.stateForm.destinationPorts?.map((protocol, index) => {

			const selectedProtocols = this.state.stateForm.destinationPorts
				.slice(0, index)
				.map(p => p.protocol);
			const availableOptions = this.state.availableProtocols.filter(
				protocol => !selectedProtocols.includes(protocol)
			);

			return (
				<Grid variant="halves" key={index} style={{ alignItems: 'center' }}>
					<Grid.Item>
						<TextAreaField
							id={`destinationPorts-${index}`}
							note='Multiple values are allowed, separated by semicolons. Port ranges are accepted.'
							value={this.state.stateForm.destinationPorts[index]?.destinationPorts}
							validation={this.formModel.fields.destinationPorts.validation}
							label={`Destination Port(s) for Protocol ${index + 1}`}
							onChange={(e) => this.handleProtocolChange(index, 'destinationPorts', e.target.value)}
							disabled={!this.formCanBeEdited()}
						/>
					</Grid.Item>
					<Grid.Item style={{ alignSelf: 'baseline' }}>
						<SelectField
							label={`Protocol ${index + 1}`}
							id={`protocol-${index}`}
							value={this.state.stateForm.destinationPorts[index]?.protocol}
							validation={this.formModel.fields.destinationPorts.validation}
							options={availableOptions}
							onChange={(e) => this.handleProtocolChange(index, 'protocol', e.target.value)}
							disabled={!this.formCanBeEdited()}
						/>
					</Grid.Item>
					<Grid.Item style={{ width: '10%' }}>
					{index != 0 &&
						<Button
							style={{ zIndex: 0 }}
							variant="primary"
							size="small"
							onClick={() => this.removeProtocol(index)}
							disabled={!this.formCanBeEdited()}
						>
							<TrashcanIcon size="small" />
						</Button>
					}
					</Grid.Item>
				</Grid>
			)
		});
	}

	wasEditted() {
		if (this.props.editingRule) {
			return (
				this.props.editingRule.description != this.stateFormHandler().description.value
				|| this.props.editingRule.action.value != this.stateFormHandler().action.value
				|| this.props.editingRule.sourceIPs != this.stateFormHandler().sourceIPs.value
				|| this.props.editingRule.destinationIPs != this.stateFormHandler().destinationIPs.value
				|| this.props.editingRule.destinationURLs != this.state.stateForm.destinationURLs
				|| this.props.editingRule.destinationPorts != this.state.stateForm.destinationPorts
				|| this.props.editingRule.sendDND != this.state.stateForm.sendDND
				|| (this.props.editingRule.operation.value == RuleOperation.Add.value && (this.props.editingRule.name != this.state.stateForm.name))
				)
			}
		return true
	}

	render() {
		return (
			<Modal show={this.props.visible}>
				<Modal.Window className="ruleModal">
					<Modal.Window.Header>
						<Modal.Window.Header.Title>
							{ this.props.isReadOnly ? 'View Rule' : !this.isModalForNewRule() ? 'Edit Rule' : 'New Rule'}
						</Modal.Window.Header.Title>
						<Modal.Window.Header.CloseButton onClick={this.props.onClose}>
							<XFilledIcon size='small' />
						</Modal.Window.Header.CloseButton>
					</Modal.Window.Header>
					<Modal.Window.Body>
						{ !this.isModalForNewRule() &&
							<ToggleField
							{...this.stateFormHandler().operation}
							label="Operation"
							disabled={this.props.isReadOnly}
							value={this.state.stateForm.operation?.value}
							options={this.props.isReadOnly ?
								[
									new RuleOperation().fromValue(this.stateFormHandler().operation.value)
								]:
								[
									RuleOperation.Modify,
									RuleOperation.Remove
								]
							}
							/>
						}
						<Grid variant="2-up">
							<Grid.Item>
								<TextInputField
									label="Rule Name"
									value={this.state.stateForm.name}
									validation={this.formModel.fields.name.validation}
									onChange={
										(event) => {
											this.handleStateFormChange('name', event.target.value);
										}
									}
									disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									{...this.stateFormHandler().region}
									options={this.getRegionOptions()}
									disabled={this.state.stateForm.operation?.name !== RuleOperation.Add.name || !this.formCanBeEdited()}
								/>
							</Grid.Item>
						</Grid>
						{this.state.stateForm.operation?.name !== RuleOperation.Remove.name &&
							<>
								<Grid variant="2-up">
									<Grid.Item>
										<ToggleField
											{...this.stateFormHandler().action}
											options= { (this.formCanBeEdited())? [
												RuleAction.Allow,
												RuleAction.Block,
											]:this.stateFormHandler().action?.value?
											[RuleAction.Allow]
											:
											[RuleAction.Block]
											}
											label="Action"
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
									<Grid.Item />
								</Grid>
								<Grid variant="2-up">
									<Grid.Item>
										<TextAreaField
											{...this.stateFormHandler().sourceIPs}											
											label="Source (IPs / IP Range / CIDR)"
											disabled={!this.formCanBeEdited()}
											note='Multiple values are allowed, separated by semicolons.'
										/>
									</Grid.Item>
									<Grid.Item>
                                        {/*<MultipleSelectField
                                            {...this.stateFormHandler().destinationIPs}
                                            options={this.getDestinationIPOptions()}
											label="Destination (IPs / IP range / CIDR)"
											disabled={!this.formCanBeEdited()}
											note='Multiple values are allowed, separated by semicolons.'
										/>*/}
										<TextAreaField
											{...this.stateFormHandler().destinationIPs}
											label="Destination (IPs / IP range / CIDR)"
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
								</Grid>
								<Grid variant="2-up">
									<Grid.Item>
										<TextAreaField
											label="Destination (URLs)"
											value={this.state.stateForm.destinationURLs}
											validation={this.formModel.fields.destinationURLs.validation}
											onChange={
												(event) => {
													this.handleStateFormChange('destinationURLs', event.target.value);
												}
											}
											note='Multiple values are allowed, separated by semicolons.'
											disabled={!this.formCanBeEdited()}
											/>
								</Grid.Item>
							</Grid>
							<Grid>
								<Grid.Item>
									<Checkbox
										className="chk-no-borders"
										{...this.stateFormHandler().sendDND}
										disabled={!this.formCanBeEdited()}
									/>
								</Grid.Item>
							</Grid>
							<Section title=''>
								{this.renderProtocols()}
								<Grid>
									<Grid.Item/>
									<Grid.Item>
										<Button
											variant="primary"
											size="small"
											style={{width: '30%', alignSelf: 'flex-end'}}
											onClick={() => this.addProtocol()}
											disabled={!this.formCanBeEdited() || this.state.stateForm?.destinationPorts.length >= MAX_PROTOCOLS}
										>
											<>
												<PlusFilledIcon size="small" />
												<span>Add Protocol</span>
											</>
										</Button>
									</Grid.Item>
								</Grid>
								</Section>
							<header className="em-c-section__header"/>
								<Grid variant="2-up">
									<Grid.Item>
										<TextAreaField
											{...this.stateFormHandler().description}
											label="Description"
											disabled={!this.formCanBeEdited()}
										/>
									</Grid.Item>
									<Grid.Item />
								</Grid>
							</>}
					</Modal.Window.Body>
					{ !this.props.isReadOnly &&
						<Modal.Window.Footer>
							<Grid variant="2-up">
								<Grid.Item>
									<Button
										variant="primary"
										disabled={(!this.formModel.isValid() || !this.wasEditted()) && this.state.stateForm.operation.name !== RuleOperation.Remove.name}					
										onClick={this.handleRequest}
									>
										{this.props.editingRule ? 'Confirm' : 'Create'}
									</Button>
								</Grid.Item>
								<Grid.Item>
									<Button
										variant="secondary"
										onClick={this.handleCancel}
									>
										Cancel
									</Button>
								</Grid.Item>
							</Grid>
						</Modal.Window.Footer>
					}
				</Modal.Window>
			</Modal>
		);
	}
}